import type { Timestamp } from 'firebase/firestore';

export const STATUSES = ['healthy', 'warning', 'failed', 'paused', 'unknown'] as const;
export type Status = (typeof STATUSES)[number];

export type Source = 'datto' | 'sp' | 'sc' | 'msp' | 'unitrends' | 'email';

export const SOURCE_LABELS: Record<Source, string> = {
  msp: 'MSP360 Backup',
  email: 'Emailed reports',
  unitrends: 'Kaseya Backup Portal',
  datto: 'Datto BCDR',
  sp: 'ShadowProtect',
  sc: 'SC Image Manager',
};

export const STATUS_LABELS: Record<Status, string> = {
  healthy: 'Healthy',
  warning: 'Warning',
  failed: 'Failed',
  paused: 'Paused',
  unknown: 'Unknown',
};

export interface StatusCounts {
  total: number;
  healthy: number;
  warning: number;
  failed: number;
  paused: number;
  unknown: number;
}

export interface Target {
  id: string;
  source: Source;
  sourceId: string;
  /** Which connection to the source found this machine. Absent means the original. */
  connectionLabel?: string;
  name: string;
  client: string;
  org: string;
  status: Status;
  statusReason: string;
  lastBackup: Timestamp | null;
  lastSuccessText: string | null;
  lastSuccessHours: number | null;
  detail: Record<string, unknown>;
  syncError: string | null;
  syncedAt: Timestamp | null;
  missingSince: Timestamp | null;
  /** Set by an owner/admin. Hidden from the dashboard and every count. */
  hidden?: boolean;
  /**
   * This machine's own backup policy - the most specific tier of the ladder.
   * Absent means it follows its client, then the tenant default.
   */
  schedulePolicy?: {
    everyHours: number | null;
    days?: number[];
    graceHours?: number | null;
    windowStart?: number | null;
    windowEnd?: number | null;
    rpoHours?: number | null;
    rpoByDay?: Record<string, number> | null;
    warningsAreSuccess?: boolean;
    missesBeforeFail?: number | null;
    note?: string;
  } | null;
  /** The open PSA ticket for this machine, if there is one. */
  psaTicketId?: string | null;
  psaTicketNumber?: string | null;
  psaTicketUrl?: string | null;
  /** Set once a machine has been missing long enough, or retired by hand. */
  retiredAt?: { toMillis?: () => number; toDate?: () => Date } | null;
  retiredReason?: 'missing' | 'manual' | null;
  retiredBy?: string | null;
  hiddenReason?: string | null;
  hiddenUntil?: Timestamp | null;
  hiddenBy?: string | null;
}

export interface Summary {
  overall: StatusCounts;
  bySource: Record<Source, StatusCounts>;
  /** When data for this source last successfully LANDED. */
  lastSyncedAt: Record<Source, Timestamp | null>;
  /** Whether the most recent ATTEMPT succeeded. */
  lastSyncOk: Record<Source, boolean>;
  /** When that attempt ran, and why it failed. Same run as lastSyncOk. */
  lastRunAt?: Record<Source, Timestamp | null>;
  lastRunError?: Record<Source, string | null>;
  updatedAt: Timestamp | null;
  thresholds: { warnHours: number; failHours: number };
}

export const EMPTY_COUNTS: StatusCounts = {
  total: 0, healthy: 0, warning: 0, failed: 0, paused: 0, unknown: 0,
};
